const GRADUATING_INTERVAL_DAYS = 1; 
const EASY_INTERVAL_DAYS = 3; 
const TARGET_BURN_COUNT = 3;

// Helper to shuffle array (Fisher-Yates)
const shuffleArray = (array) => {
    const newArr = [...array];
    for (let i = newArr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArr[i], newArr[j]] = [newArr[j], newArr[i]];
    }
    return newArr;
};

const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

// --- CLUSTERING HELPERS ---

const getParentHNU = (hnu) => {
    if (!hnu) return 'root';
    const parts = String(hnu).split('.');
    if (parts.length >= 3) return parts.slice(0, 3).join('.');
    if (parts.length > 1) return parts.slice(0, parts.length - 1).join('.');
    return 'root';
};

const clusterAndInterleave = (reviewItems, newItems, clusterSize = 4, spacing = 5) => {
    const clusters = {};
    newItems.forEach(item => {
        const parentKey = getParentHNU(item.hnu);
        if (!clusters[parentKey]) clusters[parentKey] = [];
        clusters[parentKey].push(item.id);
    });

    const clusterQueue = Object.values(clusters).map(group => {
        return group.sort((aId, bId) => 0); 
    });

    let finalQueue = [];
    let reviewIndex = 0;
    let clusterIndex = 0;

    const warmup = reviewItems.slice(0, 3);
    finalQueue.push(...warmup.map(i => i.id));
    reviewIndex = 3;

    while (reviewIndex < reviewItems.length || clusterIndex < clusterQueue.length) {
        if (clusterIndex < clusterQueue.length) {
            finalQueue.push(...clusterQueue[clusterIndex]);
            clusterIndex++;
        }
        if (reviewIndex < reviewItems.length) {
            const nextReviewChunk = reviewItems.slice(reviewIndex, reviewIndex + spacing);
            finalQueue.push(...nextReviewChunk.map(i => i.id));
            reviewIndex += spacing;
        }
    }

    return finalQueue;
};

export const getDueRuleIdsForDate = (rulesProgress, targetDateStr) => {
    const targetDate = new Date(targetDateStr);
    targetDate.setHours(23, 59, 59, 999); 
    const dueIds = [];
    Object.entries(rulesProgress).forEach(([ruleId, progress]) => {
        const srData = progress.srs_data;
        if (!srData || !srData.nextDueDate) return;
        
        const isLearning = typeof srData.learningStepIndex === 'number' && srData.learningStepIndex > -1;
        const nextDue = new Date(srData.nextDueDate);
        
        if (isLearning || nextDue <= targetDate) dueIds.push(ruleId);
    });
    return dueIds;
};

// --- EXHIBIT B: ALGORITHMIC SOURCE OF TRUTH ---
export const calculateNewSRData = (currentSrData, rating, deadlineDate) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let newData = { 
        easeFactor: 2.5, 
        currentInterval: 0, 
        repetitions: 0, 
        lapses: 0, 
        learningStepIndex: 0, 
        burnCount: 0, 
        isLeech: false, 
        ...currentSrData 
    };

    const isInLearningPhase = newData.learningStepIndex !== -1;

    if (isInLearningPhase) {
        if (rating === 'Again' || rating === 'Hard') {
            newData.lapses += 1; 
            newData.learningStepIndex = 0; 
        } else {
            newData.learningStepIndex = -1; // Graduate
            newData.currentInterval = rating === 'Good' ? 1 : 3;
        }
    } else {
        if (rating === 'Again') {
            newData.lapses += 1; 
            newData.easeFactor = Math.max(1.3, newData.easeFactor - 0.2);
            newData.learningStepIndex = 0; 
            newData.currentInterval = 0; 
        } else {
            let mult = rating === 'Hard' ? 1.2 : rating === 'Easy' ? newData.easeFactor * 1.3 : newData.easeFactor;
            if (rating === 'Hard') newData.easeFactor = Math.max(1.3, newData.easeFactor - 0.15);
            else if (rating === 'Easy') newData.easeFactor += 0.15;
            
            let newInterval = Math.max(1, Math.round(newData.currentInterval * mult));

            // BURN MODE (Deadline Compression)
            if (deadlineDate) {
                const daysUntil = Math.round((new Date(deadlineDate) - today) / (24 * 60 * 60 * 1000));
                if (daysUntil < 30 && daysUntil > 0) {
                    const maxCompressed = Math.floor(daysUntil / Math.max(1, TARGET_BURN_COUNT - newData.burnCount));
                    newInterval = Math.min(newInterval, maxCompressed);
                    if (rating === 'Good' || rating === 'Easy') newData.burnCount += 1;
                }
            }
            newData.currentInterval = newInterval;
        }
        newData.repetitions += 1;
    }
    if (newData.lapses >= 5) newData.isLeech = true;
    
    const nextDue = new Date(today);
    nextDue.setDate(nextDue.getDate() + newData.currentInterval);
    newData.nextDueDate = nextDue.toISOString();
    newData.lastReviewedDate = now.toISOString();

    return newData;
};

export const buildSessionQueue = (allRulesBySubject, rulesProgress, settings, userAccessLevel = 'free', userGoals = {}, completedNewCount = 0, fastTrackQueue = []) => {
    let workingSet = [];
    const subjects = settings.subjects.includes('all') ? Object.keys(allRulesBySubject) : settings.subjects;
    
    subjects.forEach(s => {
        if (allRulesBySubject[s]) workingSet.push(...allRulesBySubject[s].filter(i => i.itemType === 'rule' && i.id));
    });

    if (userAccessLevel === 'free') {
        workingSet = workingSet.filter(rule => rule.is_free_preview === true);
    }

    const today = new Date(); 
    today.setHours(23, 59, 59, 999);

    let learningQueue = [];
    let reviewQueue = [];
    let flaggedQueue = [];
    let hfrQueue = []; 

    for (const rule of workingSet) {
        const progress = rulesProgress[rule.id];
        const srData = progress?.srs_data;
        
        if (rule.isHFR) hfrQueue.push({ id: rule.id, due: srData?.nextDueDate ? new Date(srData.nextDueDate) : new Date(0) });
        if (progress?.is_flagged) flaggedQueue.push(rule.id);

        if (srData?.lastReviewedDate) {
            const dueDate = new Date(srData.nextDueDate);
            const isLearning = typeof srData.learningStepIndex === 'number' && srData.learningStepIndex !== -1;
            if (isLearning) learningQueue.push({ id: rule.id, due: dueDate });
            else if (dueDate <= today) reviewQueue.push({ id: rule.id, due: dueDate });
        }
    }
    
    learningQueue.sort((a, b) => a.due - b.due);
    reviewQueue.sort((a, b) => a.due - b.due);
    hfrQueue.sort((a, b) => a.due - b.due);
    
    const activeReviews = [...learningQueue, ...reviewQueue];

    const allItemsMap = new Map();
    Object.values(allRulesBySubject).flat().forEach(item => allItemsMap.set(item.id, item));
    
    const itemsToLearnToday = fastTrackQueue
        .map(id => allItemsMap.get(id))
        .filter(Boolean)
        .filter(rule => !rulesProgress[rule.id]?.srs_data?.lastReviewedDate); 

    let finalQueue = [];

    switch(settings.sessionMode) {
        case 'hfr_only':
            finalQueue = hfrQueue.map(i => i.id);
            break;
        case 'flagged_only':
            finalQueue = flaggedQueue;
            break;
        case 'new_only':
            finalQueue = itemsToLearnToday.map(i => i.id); 
            break;
        case 'cram':
             finalQueue = [...activeReviews.map(i => i.id), ...itemsToLearnToday.map(i => i.id)];
             break;
        default: 
            if (itemsToLearnToday.length > 0) {
                 finalQueue = clusterAndInterleave(activeReviews, itemsToLearnToday);
            } else {
                 finalQueue = activeReviews.map(i => i.id);
            }
            break;
    }

    return [...new Set(finalQueue)].filter(Boolean).slice(0, settings.limit);
};